import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedAdditionalDataModal } from '../Reports/reports.slice'
import Wizard from '../../widgets/Wizard'
import AccountDetails from './AccountDetails'
import VarianceRequest from './VarianceRequest'
import { showModal } from '../../widgets/modal'
import { saveCustomer, fetchPlans } from '../Customers/customers.asynActions'
import { selectUser, selectIsAdmin } from '../Profile/profile.selectors'
import {
	fetchTotals,
	fetchCommission,
	fetchUnengagedUsers,
	fetchChurnedCustomers,
	fetchProductMix,
	fetchUsersEngagementChart,
	fetchReportCustomers,
	fetchDocumentsEngagementChart,
	fetchdocumentHistoriesEngagementChart,
} from '../Reports/reports.asynActions'
import {
	selectFilters,
	selectIsDateSelected,
} from '../Reports/reports.selectors'
import { REGIONS } from '../../widgets/busyIndicator'
import { actions } from '../Account/account.slice'
import { actions as customerActions } from '../Customers/customers.slice'
import { actions as wizardActions } from '../../widgets/Wizard/wizard.slice'
import moment from 'moment/moment'

const { setPreviousFilters } = customerActions
const { resetFFLDetails } = actions
const { setIsFflNumberValid, setIsCheckboxChecked } = wizardActions
const emptyString = ''
const INVALID_CARD_NUMBER = 'Invalid card number'
const INVALID_ADDRESS = 'Index was outside'
const EXPIRED_CARD = 'Expired Card'
const INVALID_EMAIL_ADDRESS = "primary key property 'Id'"
const EMAIL_ALREADY_EXISTS = 'User Already Exist'
const INVALID_ZIP_CODE = 'invalid zip/postal'
const BAD_REQUEST_ERROR = 400
const timeOutForAlertPrompt = 3000

const getErrorMessage = (message) => {
	const IS_INVALID_CARD_NUMBER = message.includes(INVALID_CARD_NUMBER)
	const IS_INVALID_ADDRESS = message.includes(INVALID_ADDRESS)
	const IS_EXPIRED_CARD = message.includes(EXPIRED_CARD)
	const IS_INVALID_ZIP_CODE = message.includes(INVALID_ZIP_CODE)
	const IS_INVALID_EMAIL_ADDRESS =
		message.includes(INVALID_EMAIL_ADDRESS) ||
		message.includes(EMAIL_ALREADY_EXISTS)

	if (IS_INVALID_CARD_NUMBER) {
		return 'Invalid card number. Please enter the valid card number.'
	} else if (IS_INVALID_ADDRESS) {
		return 'Please enter address in valid format.(Ex: 123 Street Name, City, State, Zip code)'
	} else if (IS_EXPIRED_CARD) {
		return 'Card is expired. Please enter the valid card expiry date.'
	} else if (IS_INVALID_EMAIL_ADDRESS) {
		return 'E-mail is already in use in Account Details. Please enter new E-mail address.'
	} else if (IS_INVALID_ZIP_CODE) {
		return 'Invalid Zip Code'
	} else {
		return 'Unable to create customer. Please try again later.'
	}
}

const CreateCustomerModal = () => {
	const dispatch = useDispatch()
	const user = useSelector(selectUser)
	const IsDateSelected = useSelector(selectIsDateSelected)
	const filters = useSelector(selectFilters)
	const startDate = moment(filters.startDate)
	const endDate = moment(filters.endDate)
	const daysDifference = endDate.diff(startDate, 'days')
	const [shouldDisabledButton, setShouldDisabledButton] = useState(false)
	const [errorMessage, setErrorMessage] = useState(emptyString)
	const isAdmin = useSelector(selectIsAdmin)

	useEffect(() => {
		dispatch(showModal())
		dispatch(fetchPlans())
	}, [dispatch])

	useEffect(() => {
		errorMessage &&
			setTimeout(() => {
				setErrorMessage(emptyString)
			}, timeOutForAlertPrompt)
	}, [dispatch, errorMessage])

	const resetModal = () => {
		dispatch(setSelectedAdditionalDataModal(null))
		dispatch(resetFFLDetails())
	}

	const pages = [
		{ label: 'Account Details', component: AccountDetails },
		{ label: 'FFL Request', component: VarianceRequest },
	]

	const onFinalize = (formValues, resetWizard) => {
		setShouldDisabledButton(true)
		const {
			accountFirstName,
			accountLastName,
			accountPosition,
			accountCompany,
			accountEmail,
			accountPhoneNumber,
			accountPlanId,
			cardNumber,
			cardSecurityCode,
			cardExpiryMonth,
			cardExpiryYear,
			varianceFirstName,
			varianceLastName,
			varianceFFLNumber,
			premisesStreet,
			premisesCity,
			premisesState,
			premisesZipCode,
			mailingStreet,
			mailingCity,
			mailingState,
			mailingZipCode,
			varianceBussinessPhone,
			varianceEmail,
			varianceExpirationDate,
			varianceBusinessPhone,
			varianceTradeName,
			varianceLicenseName,
			varianceLoaIssueDate,
			varianceLoaExpirationDate,
		} = formValues

		dispatch(
			saveCustomer({
				accountFirstName,
				accountLastName,
				accountPosition,
				accountCompany,
				accountEmail,
				accountPhoneNumber,
				accountPlanId,
				backOfficeUserId: user.id,
				cardNumber,
				cardSecurityCode,
				cardExpiryMonth,
				cardExpiryYear,
				varianceFirstName,
				varianceLastName,
				varianceFFLNumber,
				varianceBussinessPhone,
				varianceEmail,
				varianceExpirationDate,
				premisesStreet,
				premisesCity,
				premisesState,
				premisesZipCode,
				mailingStreet,
				mailingCity,
				mailingState,
				mailingZipCode,
				// TODO: As of now there is no input field to add Billing Cycle so by default 1 month is passed
				BillingCycle: 1,
				varianceBusinessPhone,
				varianceTradeName,
				licenseName: varianceLicenseName,
				varianceLoaIssueDate,
				varianceLoaExpirationDate,
			})
		).then(async (res) => {
			const { payload } = res
			const isBadRequest = payload && payload.status === BAD_REQUEST_ERROR
			if (isBadRequest) {
				const formattedPayloadText = await payload.text()
				const response = payload && JSON.parse(formattedPayloadText)
				const errorMessage = response && response.message
				setErrorMessage(getErrorMessage(errorMessage))
			} else if (!isBadRequest) {
				dispatch(setIsFflNumberValid(false))
				resetModal()
				resetWizard()
				dispatch(fetchTotals({ filters, useCaching: true }))
				if (daysDifference || IsDateSelected) {
					dispatch(fetchDocumentsEngagementChart({ filters, useCaching: true }))
					dispatch(fetchUsersEngagementChart({ filters, useCaching: true }))
					dispatch(
						fetchdocumentHistoriesEngagementChart({
							filters,
							useCaching: true,
						})
					)
				}
				dispatch(fetchProductMix({ filters, useCaching: true }))
				dispatch(fetchCommission({ filters, useCaching: true }))
				dispatch(setPreviousFilters({}))
				dispatch(fetchUnengagedUsers({ filters, useCaching: true }))
				dispatch(fetchChurnedCustomers({ filters, useCaching: true }))
				dispatch(setIsCheckboxChecked(false))
				isAdmin && dispatch(fetchReportCustomers({ useCaching: true }))
			}

			setShouldDisabledButton(false)
		})
	}

	return (
		<Wizard
			pages={pages}
			onFinalize={onFinalize}
			onCancel={resetModal}
			title='Create Customer'
			finalButtonText='Create Customer'
			haveLessTopMargin={true}
			region={REGIONS.CREATE_CUSTOMER}
			shouldDisabledButton={shouldDisabledButton}
			errorMessage={errorMessage}
		/>
	)
}

export default CreateCustomerModal
